import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { CrossMarketRoute } from '../types/cross-market-route';
import { getDynamicCurrency } from '@/util-conversion/currency';
import React, { MouseEventHandler } from 'react';
import MissingImagePlaceholder from './missing-image-placeholder';
import PickupAvailability from './pickup-availability';
import ShippingDetails from './cross-market/shipping-details';
import Spacer from '@/common/components/spacer';
import VehicleSubDetails from './vehicle/vehicle-sub-details';
import VehicleTileContainer from './vehicle-tile-container';
import VehicleBadges from './vehicle-badges';

export interface VehicleTileProps {
  image?: string | React.ReactNode;
  modelYear: number;
  make: string;
  model: string;
  trim: string;
  color: string;
  price?: number;
  odometer: number;
  earliestAvailableDate: string;
  isRecentlyAdded: boolean;
  isNewVehicle: boolean;
  isElectric: boolean;
  pickupDays?: number;
  totalSavingsPerYear?: number;
  crossMarketRoute?: CrossMarketRoute;
  savingsCalculatorAvailable?: boolean;
  /** Delegate that fetches savings calculator data for the vehicle */
  fetchSavingsCalculator?: MouseEventHandler<HTMLDivElement>;
  /** Unique ID for the savings pill. This is used to assign a trigger for the coachmark. */
  savingsV2PillId: string;
  /** If true, a coach mark popover will be shown on the savings pill */
  showCoachMark?: boolean;
  /** Indicates if image loading into DOM should take priority */
  imageLoadPriority?: 'high' | 'low';
}

export default function VehicleTile({
  image,
  modelYear,
  make,
  model,
  trim,
  color,
  price,
  odometer,
  earliestAvailableDate,
  isRecentlyAdded,
  isElectric,
  isNewVehicle,
  pickupDays,
  totalSavingsPerYear,
  crossMarketRoute,
  savingsCalculatorAvailable = false,
  fetchSavingsCalculator,
  savingsV2PillId,
  showCoachMark,
  imageLoadPriority = 'low',
}: VehicleTileProps) {
  const isHighPriorityImage = imageLoadPriority === 'high';

  return (
    <VehicleTileContainer>
      <VehicleBadges
        isNewVehicle={isNewVehicle}
        isElectric={isElectric}
        isRecentlyAdded={isRecentlyAdded}
        savingsCalculatorAvailable={savingsCalculatorAvailable}
        totalSavingsPerYear={totalSavingsPerYear}
        savingsV2PillId={savingsV2PillId}
        showCoachMark={showCoachMark}
        fetchSavingsCalculator={fetchSavingsCalculator}
      />

      {!image ? (
        <AspectRatio ratio={4 / 3} className="h-full w-full px-2 pt-2">
          <MissingImagePlaceholder />
        </AspectRatio>
      ) : typeof image === 'string' ? (
        <AspectRatio ratio={4 / 3}>
          <img
            src={image}
            alt={`${modelYear} ${make} ${model}`}
            fetchPriority={isHighPriorityImage ? 'high' : 'auto'}
            loading={isHighPriorityImage ? 'eager' : 'lazy'}
            className="h-full w-full"
          />
        </AspectRatio>
      ) : (
        image
      )}

      <Spacer variant="height24" />

      <div className="px-[24px]">
        <p className="body-strong">
          {modelYear}・{make} {model}
        </p>

        <VehicleSubDetails trim={trim} odometer={odometer} color={color} />

        <Spacer variant="height8" />

        {price ? <h3>{getDynamicCurrency(price)}/mo</h3> : null}
      </div>

      <Spacer variant="height16" />

      <div className="h-px w-full bg-flexcar-neutral80" />

      <div className="px-[24px]">
        <div className="flex flex-row justify-between">
          {/* Due to shipping details being a clickable element that gets extra padding to the clickable dom element itself, we add padding here as well to match */}
          <div className="py-[16px]">
            <PickupAvailability
              pickupDays={pickupDays}
              earliestAvailableDate={earliestAvailableDate}
            />
          </div>

          {crossMarketRoute?.fee ? (
            <ShippingDetails
              fee={crossMarketRoute.fee}
              originMarketName={crossMarketRoute.origin_market_name}
              destinationMarketName={crossMarketRoute.destination_market_name}
              contentAlign={'end'}
              variant="body3"
            />
          ) : null}
        </div>
      </div>
    </VehicleTileContainer>
  );
}
